<template>
  <v-card class="mt-4">
    <v-card-text>
      <v-row>
        <v-col v-for="subType in subTypes" :key="subType" cols="12" sm="6" md="4" lg="3">
          <div class="mb-4">
            <h3 class="mb-3">
              {{ getSubTypeLabel(subType) }}
              <v-btn small color="primary" @click="openAddDialog(subType)">
                <v-icon small left>mdi-plus</v-icon>
                新增
              </v-btn>
            </h3>
            <OptionsList
              :type="'crm'"
              :sub-type="subType"
              :initial-options="getOptionsForSubType(subType)"
              @reload="reload"
            />
          </div>
        </v-col>
      </v-row>
    </v-card-text>

    <!-- Add/Edit Dialog -->
    <v-dialog v-model="addDialog" max-width="500px">
      <v-card>
        <v-card-title>新增選項</v-card-title>
        <v-card-text>
          <ValidationObserver ref="form" v-slot="{ invalid }">
            <ValidationProvider v-slot="{ errors }" name="名稱" rules="required">
              <v-text-field
                v-model="addForm.name"
                :error="!!errors.length"
                :error-messages="errors"
                label="名稱 *"
                outlined
                dense
              />
            </ValidationProvider>
            <ValidationProvider v-slot="{ errors }" name="排序" rules="numeric">
              <v-text-field
                v-model.number="addForm.order"
                :error="!!errors.length"
                :error-messages="errors"
                label="排序"
                outlined
                dense
                type="number"
                hint="數字越小越靠前"
              />
            </ValidationProvider>
          </ValidationObserver>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="closeDialog">取消</v-btn>
          <v-btn color="primary" @click="saveOption">儲存</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import OptionsList from '@/components/Options/OptionsList';
import { createOption, getOptions } from '@/api/options';
import api from '@/api';
import { mapActions } from 'vuex';
import { translateOptions } from '@/services/filters';
import { crmOptionsSubTypesOptions } from '@/data/optionsConstants';

export default {
  name: 'CRMOptions',
  components: {
    OptionsList,
  },
  data() {
    return {
      subTypes: [],
      allOptions: [], // Store all options to avoid multiple fetches
      addDialog: false,
      addForm: {
        type: 'crm',
        subType: '',
        subTypeOrder: 0,
        name: '',
        order: 0,
      },
    };
  },
  async mounted() {
    await this.loadSubTypes();
  },
  methods: {
    ...mapActions('alert', ['updateAlertMessage']),
    getSubTypeLabel(subType) {
      return translateOptions(subType, crmOptionsSubTypesOptions, 'value', 'text') || subType;
    },
    getOptionsForSubType(subType) {
      // Filter options for specific subType and sort
      return this.allOptions
        .filter(opt => opt.type === 'crm' && opt.subType === subType)
        .sort((a, b) => {
          if (a.order !== b.order) return a.order - b.order;
          return new Date(b.createdAt) - new Date(a.createdAt);
        });
    },
    async loadSubTypes() {
      try {
        const response = await getOptions(api, { filter: { type: 'crm' } });
        const options = response.docs || response;

        // Store all options for reference (avoid multiple fetches)
        this.allOptions = options;

        // Get unique subTypes for CRM sorted by subTypeOrder
        const crmOptions = options;
        const crmSubTypeMap = new Map();
        crmOptions.forEach(opt => {
          const subType = opt.subType || '';
          if (!crmSubTypeMap.has(subType)) {
            crmSubTypeMap.set(subType, opt.subTypeOrder || 0);
          }
        });
        this.subTypes = Array.from(crmSubTypeMap.entries())
          .sort((a, b) => a[1] - b[1])
          .map(entry => entry[0]);
      } catch (e) {
        console.error('Failed to load subTypes:', e);
      }
    },
    openAddDialog(subType) {
      // Get the subTypeOrder from existing options with the same subType
      const existingOptions = this.allOptions.filter(
        opt => opt.type === 'crm' && opt.subType === subType,
      );
      const subTypeOrder = existingOptions.length > 0 ? existingOptions[0].subTypeOrder || 0 : 0;

      this.addForm = {
        type: 'crm',
        subType,
        subTypeOrder,
        name: '',
        order: 0,
      };
      this.addDialog = true;
    },
    closeDialog() {
      this.addDialog = false;
      this.$refs.form?.reset();
    },
    async saveOption() {
      try {
        if (!(await this.$refs.form?.validate?.())) {
          return;
        }

        await createOption(api, this.addForm);
        await this.updateAlertMessage({
          msg: '新增成功',
          type: 'success',
          color: 'success',
        });

        this.closeDialog();
        await this.loadSubTypes();
        this.reload();
      } catch (e) {
        await this.updateAlertMessage({
          msg: e.message || '操作失敗',
          type: 'error',
          color: 'error',
        });
      }
    },
    reload() {
      // Trigger reload for all lists
      this.$root.$emit('reload-options');
    },
  },
};
</script>

<style scoped>
h3 {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
</style>
